<script setup lang="ts">
import { getPriceWithCurrency } from '~/helpers/priceHelper'
import { conciergerieKeypoints } from '~/helpers/house/conciergeServiceOffers'

import type { HouseDetails } from '~/types/house/types'
import type { SupportedCurrencies } from 'lc-services/types'

const { locale } = useI18n()
const { userIsPartner } = useAuth()
const { booking } = useHouseInquiryBooking()

const props = withDefaults(
  defineProps<{
    currency: SupportedCurrencies
    house: HouseDetails
    minimumPriceWithoutOpCosts?: number
    nightCount: number
    publicPrice: number
    validPrice: boolean
  }>(),
  {
    minimumPriceWithoutOpCosts: 0,
  },
)

const showPrice = computed(() => props.minimumPriceWithoutOpCosts > 0)

const currencyTotalPrice = computed(() => {
  let totalPrice = null

  if (props.validPrice) {
    totalPrice = getPriceWithCurrency({
      price: props.publicPrice,
      currency: props.currency,
      i18n: { locale },
    })
  }

  if (!booking.value.price.definitivePrice) return `${totalPrice}*`

  return totalPrice
})

const showReinsuranceBlock = computed(
  () => !booking.value.price.definitivePrice && booking.value.price.value,
)
</script>

<template>
  <div class="md:mb-6">
    <div v-if="$device.isDesktop">
      <ul>
        <li class="mb-2 flex items-center justify-between last:mb-0">
          <div class="flex items-center">
            <BaseIcon class="mr-3" name="check" />
            <span class="font-medium">
              {{ $t(`house.conciergerieOffer.${house.conciergeServiceOffer}`) }}
            </span>
          </div>
          <base-button
            class="text-md"
            color="black-underline"
            font-weight="font-normal"
            variant="link"
            @click="useScrollTo({ id: 'house-concierge', offset: -70 })"
          >
            {{ $t('house.conciergerieOffer.moreDetails') }}
          </base-button>
        </li>
        <li class="mb-2 flex items-center last:mb-0">
          <BaseIcon class="mr-3 flex-none" name="check" />
          <span class="font-medium">
            {{ $t('house.conciergerieOffer.cleaningIncluded') }}
          </span>
        </li>
      </ul>
    </div>

    <div v-else>
      <div
        class="mb-3 flex items-center justify-between text-md lg:text-base"
        data-testid="inquiry-block-total__conciergerie"
      >
        <span class="font-medium">
          {{ $t(`house.conciergerieOffer.${house.conciergeServiceOffer}`) }}
        </span>

        <span class="font-bold">
          {{ $t('global.included') }}
        </span>
      </div>

      <ul class="md:pl-3">
        <li
          v-for="(keypoint, i) in conciergerieKeypoints[
            house.conciergeServiceOffer
          ]"
          :key="i"
          class="mb-2 flex items-center last:mb-0"
        >
          <BaseIcon class="mr-3 flex-none" name="check" />
          <span class="text-md text-gray-600 lg:text-base">
            {{ $t(`house.conciergerieOffer.keypoints.${keypoint}`) }}
          </span>
        </li>
      </ul>

      <div class="mb-3 flex items-center justify-between text-md lg:text-base">
        <span class="font-medium">
          {{ $t('house.conciergerieOffer.cleaning') }}
        </span>

        <span class="font-bold">
          {{ $t('global.included') }}
        </span>
      </div>
    </div>

    <div
      class="mt-6 flex justify-between border-t border-gray-200 pt-6 font-bold lg:text-lg"
    >
      <div>
        <span>{{ $t('house.total') }}</span>
        <span data-testid="inquiry-block-total__nights">
          {{ $t('house.days_between', nightCount) }}
        </span>
      </div>
      <div data-testid="inquiry-block-total__price-total">
        <span v-if="showPrice && house.displayPrices && validPrice">
          <template v-if="userIsPartner">{{ $t('search.from') }}</template>
          {{ currencyTotalPrice }}
        </span>
        <span v-else>
          {{ $t('house.price_upon_request') }}
        </span>
      </div>
    </div>

    <p
      v-if="showPrice"
      class="mb-0 mt-2 text-sm font-normal italic text-gray-500"
      data-testid="inquiry-block-total__fees"
    >
      <template
        v-if="
          showReinsuranceBlock && ![168].includes(Number(house.destinationId))
        "
      >
        <span v-if="house.publicTaxes > 0">
          {{ $t('inquiry.localTaxesIncludedAndFeesExcluded') }}
        </span>
        <span v-else>{{ $t('inquiry.taxesAndFeesExcluded') }}</span>
      </template>

      <template v-else-if="![168].includes(Number(house.destinationId))">
        <span v-if="house.publicTaxes > 0">
          {{ $t('inquiry.localTaxesIncludedAndFeesExcludedReinsurance') }}
        </span>
        <span v-else>{{ $t('inquiry.taxesAndFeesExcludedReinsurance') }}</span>
      </template>

      <span v-if="[168].includes(Number(house.destinationId))">
        {{ $t('house.additional_charges') }}
      </span>
    </p>

    <div v-show="showReinsuranceBlock" class="mt-2 bg-gray-100 p-2 text-md">
      {{ $t('inquiry.reinsurranceBlock[0]') }}
      {{ $t('inquiry.reinsurranceBlock[1]') }}
    </div>
  </div>
</template>
